import useCreateTag from "api/api-hooks/tags/useCreateTag";
import useUpdateTag from "api/api-hooks/tags/useUpdateTag";
import AppColorPicker from "elements/AppColorPicker";
import RequiredTitle from "elements/RequiredTitle";
import { useEffect, useState } from "react";
import { Alert, Button, FormGroup, Input } from "reactstrap";
import AppSpinner from "../../elements/AppSpinner";
import AppDrawer from "../Drawers/AppDrawer";

const initialTagData = () => ({
  Name: "",
  Description: "",
  TagGroup: "",
  Color: "#e3e7e4",
});

/**
 * SaveTagDrawer - Drawer used for creating / updating tags in any scope.
 *
 * @param {boolean} open - Whether the drawer is open or not.
 * @param {function} onClose - Function that closes the drawer (usually updates open prop to false)
 * @param {string} scope - The scope in which we are adding a tag (Cloud, Workspace, Module, or Person)
 * @param {string?} groupName  - The default group the tag will be in
 * @param {object?} existingTag - An existing tag - This means we are editing it. (Drawer in edit mode)
 * @param {string?} color - The color the tag should be (User can still changed) Used primarily for tag groups.
 * @param {boolean} fromSelectableInput - This drawer was opened from a SelectableInput (Your Tags) - So saving will need to be handled differently.
 */
const SaveTagDrawer = ({
  open,
  onClose,
  onExited,
  scope,
  groupName,
  existingTag = null,
  color = null,
  fromSelectableInput = false,
}) => {
  const [tag, setTag] = useState(initialTagData());
  const [error, setError] = useState("");
  const { createTag, creatingTag, createError } =
    useCreateTag(fromSelectableInput);
  const { updateTag, updatingTag, updateError } = useUpdateTag();

  const handleTagUpdate = (e) => {
    const { name, value } = e.target;
    setError("");

    setTag((prevTag) => ({ ...prevTag, [name]: value }));
  };

  const handleClose = () => {
    setTag(initialTagData());
    onClose();
  };

  const handleSave = () => {
    if (existingTag) {
      // Create payload containing all values that have changed
      // Only Name, Description, TagGroup, and Color are allowed to change
      const payload = {};

      if (tag.Name !== existingTag.Name) {
        payload.Name = tag.Name;
      }

      if (tag.Description !== existingTag.Description) {
        payload.Description = tag.Description;
      }

      if (tag.TagGroup !== existingTag.TagGroup) {
        payload.TagGroup = tag.TagGroup;
      }

      if (tag.Color !== existingTag.Color) {
        payload.Color = tag.Color;
      }

      updateTag({
        scope: scope,
        payload: payload,
        tagID: existingTag.id,
        onSuccess: handleClose,
      });
    } else {
      createTag({
        scope: scope,
        payload: tag,
        onSuccess: handleClose,
      });
    }
  };

  // Sync existing tag with local state if existingTag is passed in
  useEffect(() => {
    if (!existingTag) return;

    setTag(existingTag);
  }, [existingTag]);

  // Sync groupName with local state if groupName is passed in
  useEffect(() => {
    if (groupName) setTag((prevTag) => ({ ...prevTag, TagGroup: groupName }));
  }, [groupName]);

  // Sync color with local state is color is passed in
  useEffect(() => {
    if (color) setTag((prev) => ({ ...prev, Color: color }));
  }, [color]);

  useEffect(() => {
    if (updateError) {
      setError(updateError);
    }
    if (createError) {
      setError(createError);
    }
  }, [createError, updateError]);

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      onExited={onExited}
      headerTitle={existingTag ? "Update Tag" : "Create Tag"}
      headerOptions={
        <Button
          style={{ width: 70 }}
          size="sm"
          color="primary"
          onClick={handleSave}
          disabled={creatingTag || updatingTag || !tag.Name}
        >
          {creatingTag || updatingTag ? (
            <AppSpinner size="sm" color="white" />
          ) : existingTag ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      }
      body={
        <>
          {error && (
            <Alert className="mb-4" color="warning">
              {error}
            </Alert>
          )}
          <FormGroup>
            <RequiredTitle title="Name" />
            <Input
              placeholder="Name"
              name="Name"
              value={tag.Name}
              onChange={handleTagUpdate}
            />
          </FormGroup>
          <FormGroup>
            <h5>Description</h5>
            <Input
              placeholder="Description"
              name={"Description"}
              value={tag.Description}
              onChange={handleTagUpdate}
              type="textarea"
            />
          </FormGroup>
          <FormGroup>
            <h5>Group</h5>
            <Input
              placeholder="Group"
              name={"TagGroup"}
              value={tag.TagGroup}
              onChange={handleTagUpdate}
            />
          </FormGroup>
          <FormGroup>
            <AppColorPicker
              title="Color"
              value={color ? color : tag.Color}
              onChange={(val) => {
                handleTagUpdate({ target: { name: "Color", value: val } });
              }}
            />
          </FormGroup>
        </>
      }
    />
  );
};

export default SaveTagDrawer;
